
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Actions from '../../../components/docs/Actions';
import Home from '../../../components/docs/Home';
import Guide from 'plaid-threads/Icons/Guide';
import Libraries from 'plaid-threads/Icons/Libraries';
import Rocket from 'plaid-threads/Icons/Rocket';
import styles from './core-exchange.module.scss';
export const metadata = {
  toc: false,
  section: 'Core Exchange',
  title: 'Welcome',
  description: "Build an FDX-aligned API with Plaid's free open finance solution",
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Welcome' subtitle={metadata.description} mdxType="Header"></Header>
      <h2 {...{
        "id": "what-this-guide-contains"
      }}>{`What this guide contains`}</h2>
      <p>{`This documentation contains everything you need to build and launch your FDX-aligned API. It includes:`}</p>
      <ul>
        <li parentName="ul">{`An `}<a parentName="li" {...{
            "href": "/core-exchange/docs/onboarding-guide"
          }}>{`onboarding guide`}</a>{` covering the end-to-end integration journey, from setup through post-launch`}</li>
        <li parentName="ul">{`A `}<a parentName="li" {...{
            "href": "/core-exchange/docs/implementation-checklist"
          }}>{`checklist`}</a>{` of all the steps that must be completed during each stage of development`}</li>
        <li parentName="ul">{`A `}<a parentName="li" {...{
            "href": "/core-exchange/docs/user-experience"
          }}>{`visual walkthrough of the experience`}</a>{` your customers will have once Plaid is fully integrated to your FDX-aligned API`}</li>
        <li parentName="ul">{`An in-depth explanation of the `}<a parentName="li" {...{
            "href": "/core-exchange/docs/authentication"
          }}>{`OAuth-compliant authentication process`}</a>{`, including `}<a parentName="li" {...{
            "href": "/core-exchange/docs/authentication#try-the-interactive-demo"
          }}>{`FDX Explorer`}</a>{`, which provides an interactive demo of the full OAuth flow and Core Exchange API calls`}</li>
        <li parentName="ul">{`Example requests you can send to the `}<a parentName="li" {...{
            "href": "/core-exchange/docs/mock-server"
          }}>{`mock server`}</a>{` to get familiar with FDX data payloads`}</li>
        <li parentName="ul">{`An `}<a parentName="li" {...{
            "href": "/core-exchange/docs/reference"
          }}>{`API reference`}</a>{` with sample requests, responses, and required fields for all FDX-aligned spec endpoints`}</li>
        <li parentName="ul">{`A `}<a parentName="li" {...{
            "href": "/core-exchange/docs/data-mapping-guide"
          }}>{`data mapping guide`}</a>{` including a FAQ and how to avoid common mistakes`}</li>
      </ul>
      <h2 {...{
        "id": "core-exchange"
      }}>{`Core Exchange`}</h2>
      <p>{`Core Exchange is Plaid's free, FDX-aligned API specification scoped to the data models and endpoints that power Plaid downstream products. It includes developer documentation, tooling in the Data Partner Dashboard, and dedicated support from the Plaid team.`}</p>
      <p>{`For more details, see the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/onboarding-guide"
        }}>{`onboarding guide`}</a>{`.`}</p>
      <h2 {...{
        "id": "consent-management"
      }}>{`Consent management`}</h2>
      <p>{`Consent management gives data providers the tools to gain visibility into the authorization details for connections their customers have made via Plaid, build a consent portal that enables customers to manage their connections, and receive real-time alerts.`}</p>
      <Image src='/assets/img/core-exchange/permissions-manager/lookup.png' alt='Dashboard search interface for looking up customer connections' caption='Search by customer to view authorization details' expandable={true} mdxType="Image" />
      <p>{`For more details, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management"
        }}>{`Consent management`}</a>{`.`}</p>
      <h2 {...{
        "id": "app-directory"
      }}>{`App Directory`}</h2>
      <p>{`App Directory gives data providers insights about the thousands of apps on Plaid's network that their customers have connected to, including categories and number of connections. App Directory is FDX-aligned and available via both the no-code Dashboard and APIs.`}</p>
      <Image src='/assets/img/core-exchange/app-directory.png' alt='App Directory showing connected applications with categories and connection counts' caption='View connected applications and their details' expandable={true} mdxType="Image" />
      <p>{`For more details, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/permissions-manager"
        }}>{`App Directory`}</a>{`.`}</p>
      <h2 {...{
        "id": "building-with-plaid"
      }}>{`Building with Plaid`}</h2>
      <p>{`When you build your API with Plaid, your institution becomes available to Plaid-powered applications. If your institution is not already supported in Plaid Link, it will appear once your API is validated and launched. If your institution is already supported, API connectivity generally leads to better conversion and longer-lasting connections for your end users.`}</p>
      <p>{`Building your FDX-aligned API with Plaid helps your organization align with industry standards for consumer-authorized data sharing.`}</p>
      <h2 {...{
        "id": "security-and-privacy"
      }}>{`Security and privacy`}</h2>
      <p>{`Plaid has established security and privacy processes to help keep your customers' data secure, including TLS 1.2+ enforcement, mTLS support, IP allowlisting, and OAuth with PKCE for public clients. For more details, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/security"
        }}>{`Security best practices`}</a>{` and the `}<a parentName="p" {...{
          "href": "https://security.plaid.com/"
        }}>{`Plaid Security Portal`}</a>{`.`}</p>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "what-this-guide-contains",
        level: 2,
        title: "What this guide contains",
      },
{
        id: "core-exchange",
        level: 2,
        title: "Core Exchange",
      },
{
        id: "consent-management",
        level: 2,
        title: "Consent management",
      },
{
        id: "app-directory",
        level: 2,
        title: "App Directory",
      },
{
        id: "building-with-plaid",
        level: 2,
        title: "Building with Plaid",
      },
{
        id: "security-and-privacy",
        level: 2,
        title: "Security and privacy",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
